import {
  Background,
  BackgroundVariant,
  Controls,
  Panel,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from "@xyflow/react";
import type { MouseEvent, ReactNode } from "react";
import "@xyflow/react/dist/style.css";

import { ArchitectureMapLegend } from "@/presentation/components/architecture/architecture-map-legend";
import { ComponentMapNode } from "@/presentation/components/architecture/component-map-node";

const NODE_TYPES = { mapComponent: ComponentMapNode };

interface ArchitectureMapCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onNodeClick: (event: MouseEvent, node: Node) => void;
  onPaneClick: () => void;
  /** Editable mode: allow node→node edge creation + edge deletion via key. */
  editable?: boolean;
  onConnect?: (connection: Connection) => void;
  onEdgesChange?: (changes: EdgeChange[]) => void;
  /** Extra UI to render in the top-right panel (e.g. live-edit toolbar). */
  topRightPanel?: ReactNode;
  /** Replaces the default legend when provided (e.g. a "live edit" pill). */
  topCenterPanel?: ReactNode;
}

export function ArchitectureMapCanvas({
  nodes,
  edges,
  onNodesChange,
  onNodeClick,
  onPaneClick,
  editable = false,
  onConnect,
  onEdgesChange,
  topRightPanel,
  topCenterPanel,
}: ArchitectureMapCanvasProps) {
  return (
    <div
      data-slot="architecture-map-canvas"
      data-editable={editable}
      className="glow-card-static relative min-h-[min(82vh,1120px)] w-full overflow-hidden rounded-xl"
    >
      <div className="relative h-full min-h-[min(82vh,1120px)] w-full">
        <ReactFlow
          className="h-full min-h-[min(82vh,1120px)] w-full"
          zIndexMode="manual"
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={NODE_TYPES}
          fitView
          fitViewOptions={{ padding: 0.32, maxZoom: 0.92 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable
          nodesConnectable={editable}
          edgesFocusable={editable}
          elementsSelectable
          deleteKeyCode={editable ? ["Backspace", "Delete"] : null}
          minZoom={0.1}
          maxZoom={1.45}
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={36}
            size={1}
            color="oklch(0.5 0.02 240 / 0.14)"
          />
          <Panel position="top-center" className="!m-3">
            {topCenterPanel ?? <ArchitectureMapLegend />}
          </Panel>
          {topRightPanel ? (
            <Panel position="top-right" className="!m-3">
              {topRightPanel}
            </Panel>
          ) : null}
          <Controls
            position="bottom-right"
            showInteractive={false}
            className="!border-none !bg-transparent"
          />
        </ReactFlow>
      </div>
    </div>
  );
}
